import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, WarningCircle, Eye, ArrowCounterClockwise, Trash, FileArrowUp, PencilSimple, SealCheck, GraduationCap } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCStatusBadge, fmtAmt } from "../components/cc/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";

const CONTROL_ICON = {
  pass: <CheckCircle size={18} weight="fill" className="text-emerald-600" />,
  fail: <XCircle size={18} weight="fill" className="text-red-600" />,
  missing: <WarningCircle size={18} weight="fill" className="text-amber-500" />,
};

function RowEditDialog({ row, batch, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (row) setForm({
      policy_number: row.policy_number || "", insured_name: row.insured_name || "",
      transaction_type: row.transaction_type || "", transaction_date: row.transaction_date || "",
      premium: row.premium || "", amount: row.amount || "", assigned_producer: row.assigned_producer || "",
      row_type: row.row_type, excluded: row.excluded, exclude_reason: row.exclude_reason || "",
    });
  }, [row]);
  if (!row) return null;
  const isStmt = batch.doc_type === "carrier_statement";

  const save = async () => {
    const orig = {
      policy_number: row.policy_number || "", insured_name: row.insured_name || "",
      transaction_type: row.transaction_type || "", transaction_date: row.transaction_date || "",
      premium: row.premium || "", amount: row.amount || "", assigned_producer: row.assigned_producer || "",
      row_type: row.row_type, excluded: row.excluded, exclude_reason: row.exclude_reason || "",
    };
    const payload = {};
    for (const k of Object.keys(form)) {
      if (form[k] !== orig[k]) payload[k] = form[k];
    }
    if (!Object.keys(payload).length) {
      toast.info("No changes made — use the Verify button to confirm the row as-is");
      return;
    }
    if ("amount" in payload && payload.amount === "") {
      toast.error("Commission amount can't be blank — enter a signed value or exclude the row");
      return;
    }
    try {
      await api.put(`/commission/imports/${batch.id}/rows/${row.id}`, payload);
      toast.success("Row corrected & verified");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Correct row — page {row.page}, #{row.row_index}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 bg-slate-50 rounded p-2 font-mono break-all">{row.raw_text || "(no raw text)"}</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Policy number</label>
            <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} data-testid="cc-row-policy-input" /></div>
          <div><label className="text-xs text-slate-500">Insured name</label>
            <Input value={form.insured_name} onChange={(e) => setForm({ ...form, insured_name: e.target.value })} data-testid="cc-row-insured-input" /></div>
          <div><label className="text-xs text-slate-500">Transaction type</label>
            <Select value={form.transaction_type || "__blank__"}
              onValueChange={(v) => setForm({ ...form, transaction_type: v === "__blank__" ? "" : v })}>
              <SelectTrigger data-testid="cc-row-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__blank__">(blank — auto-classify)</SelectItem>
                {["New Business", "Renewal", "Endorsement", "Reinstatement", "Cancellation", "Chargeback"]
                  .map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                {form.transaction_type &&
                  !["New Business", "Renewal", "Endorsement", "Reinstatement", "Cancellation", "Chargeback"].includes(form.transaction_type) && (
                    <SelectItem value={form.transaction_type}>{form.transaction_type} (as extracted)</SelectItem>
                  )}
              </SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">Date (YYYY-MM-DD)</label>
            <Input value={form.transaction_date} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} data-testid="cc-row-date-input" /></div>
          <div><label className="text-xs text-slate-500">Premium</label>
            <Input value={form.premium} onChange={(e) => setForm({ ...form, premium: e.target.value })} data-testid="cc-row-premium-input" /></div>
          {isStmt ? (
            <div><label className="text-xs text-slate-500">Commission amount (signed)</label>
              <Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="-123.45" data-testid="cc-row-amount-input" /></div>
          ) : (
            <div><label className="text-xs text-slate-500">Assigned producer</label>
              <Input value={form.assigned_producer} onChange={(e) => setForm({ ...form, assigned_producer: e.target.value })} data-testid="cc-row-producer-input" /></div>
          )}
          <div><label className="text-xs text-slate-500">Row type</label>
            <Select value={form.row_type} onValueChange={(v) => setForm({ ...form, row_type: v })}>
              <SelectTrigger data-testid="cc-row-rowtype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["transaction", "subtotal", "header", "footer", "other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="flex items-center gap-2 pt-5">
            <Switch checked={form.excluded} onCheckedChange={(v) => setForm({ ...form, excluded: v })} data-testid="cc-row-exclude-switch" />
            <span className="text-sm">Exclude from totals</span>
          </div>
        </div>
        {form.excluded && (
          <div><label className="text-xs text-slate-500">Exclusion reason (required)</label>
            <Input value={form.exclude_reason} onChange={(e) => setForm({ ...form, exclude_reason: e.target.value })} data-testid="cc-row-exclude-reason-input" /></div>
        )}
        {row.original && <p className="text-[11px] text-slate-400">As extracted: {Object.entries(row.original).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ") || "—"}</p>}
        <Button onClick={save} disabled={form.excluded && !form.exclude_reason} data-testid="cc-row-save-btn">Save correction (marks verified)</Button>
      </DialogContent>
    </Dialog>
  );
}

function AddRowDialog({ batch, open, onClose, onSaved }) {
  const empty = { policy_number: "", insured_name: "", transaction_type: "", transaction_date: "", amount: "", note: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/commission/imports/${batch.id}/rows`, {
        ...form, transaction_date: form.transaction_date || null });
      toast.success("Row added — totals recounted");
      onClose();
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cc-add-row-dialog">
        <DialogHeader><DialogTitle>Add a missing row</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">For detail lines the extraction missed on the original statement. The row is marked as human-entered and audited.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Policy number</label>
            <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} data-testid="cc-add-row-policy" /></div>
          <div><label className="text-xs text-slate-500">Insured name</label>
            <Input value={form.insured_name} onChange={(e) => setForm({ ...form, insured_name: e.target.value })} data-testid="cc-add-row-insured" /></div>
          <div><label className="text-xs text-slate-500">Transaction type</label>
            <Select value={form.transaction_type || "__blank__"}
              onValueChange={(v) => setForm({ ...form, transaction_type: v === "__blank__" ? "" : v })}>
              <SelectTrigger data-testid="cc-add-row-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__blank__">(blank — auto-classify)</SelectItem>
                {["New Business", "Renewal", "Endorsement", "Reinstatement", "Cancellation", "Chargeback"]
                  .map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">Transaction date</label>
            <Input type="date" value={form.transaction_date} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} data-testid="cc-add-row-date" /></div>
          <div><label className="text-xs text-slate-500">Commission amount</label>
            <Input placeholder="e.g. -197.25" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cc-add-row-amount" /></div>
        </div>
        <div><label className="text-xs text-slate-500">Where is this row on the original? (required)</label>
          <Input placeholder="e.g. page 2, 5th row — WILLIAM SHIREY reinstatement" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="cc-add-row-note" /></div>
        <Button onClick={save} disabled={saving || !form.amount || form.note.trim().length < 10} data-testid="cc-add-row-save">Add row</Button>
      </DialogContent>
    </Dialog>
  );
}

const LAYOUT_FIELD_OPTS = [
  { v: "ignore", l: "— ignore —" },
  { v: "policy_number", l: "Policy number" },
  { v: "insured_name", l: "Insured name" },
  { v: "amount", l: "Commission amount" },
  { v: "transaction_type", l: "Transaction type" },
  { v: "transaction_date", l: "Transaction date" },
  { v: "premium", l: "Premium" },
  { v: "effective_date", l: "Effective date" },
  { v: "expiration_date", l: "Expiration date" },
  { v: "carrier_name_raw", l: "Carrier (per-row, MGA)" },
  { v: "assigned_producer", l: "Assigned producer" },
  { v: "policy_labels", l: "Policy labels" },
];

function TeachLayoutDialog({ batch, open, onClose, onSaved }) {
  const [cols, setCols] = useState(null);
  const [hasLayout, setHasLayout] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCols(null); setErr("");
    api.get(`/commission/imports/${batch.id}/headers`)
      .then((r) => {
        setCols(r.data.columns.map((c) => ({ ...c, field: c.field || "ignore" })));
        setHasLayout(r.data.has_custom_layout);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Could not read the file"));
  }, [open, batch.id]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/commission/carriers/${batch.carrier_id}/layout`,
        { labels: cols.map((c) => ({ label: c.raw || c.label, field: c.field })), batch_id: batch.id });
      toast.success(data.reextracted
        ? `Layout saved for ${batch.carrier_name} — re-reading this statement with your mapping…`
        : `Layout saved for ${batch.carrier_name}`);
      onClose();
      setTimeout(onSaved, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try {
      await api.delete(`/commission/carriers/${batch.carrier_id}/layout`);
      toast.success("Taught layout removed — automatic detection applies again");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const mappedCount = (cols || []).filter((c) => c.field !== "ignore").length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="cc-teach-layout-dialog">
        <DialogHeader><DialogTitle>Teach {batch.carrier_name}'s layout</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">
          Tell the system what each column means, once. Every future {batch.carrier_name} statement will be read with this map automatically.
        </p>
        {err && <p className="text-sm text-red-600" data-testid="cc-teach-error">{err}</p>}
        {!err && !cols && <p className="text-sm text-slate-400 animate-pulse">Reading the file…</p>}
        {cols && (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {cols.map((c, i) => (
              <div key={c.index} className="flex items-center gap-3 px-3 py-2" data-testid={`cc-teach-col-${c.index}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{c.raw || <span className="text-slate-400">(column {c.index + 1})</span>}</p>
                  {c.samples.length > 0 && <p className="text-[11px] text-slate-400 truncate">e.g. {c.samples.join(" · ")}</p>}
                </div>
                <Select value={c.field} onValueChange={(v) => setCols(cols.map((x, j) => (j === i ? { ...x, field: v } : x)))}>
                  <SelectTrigger className="w-52 h-8 text-xs shrink-0" data-testid={`cc-teach-field-${c.index}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LAYOUT_FIELD_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving || !cols || mappedCount === 0} data-testid="cc-teach-save-btn">
            Save layout & re-read statement
          </Button>
          {hasLayout && (
            <Button variant="ghost" className="text-slate-500" onClick={reset} data-testid="cc-teach-reset-btn">Reset to automatic</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function CommissionBatchPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("flagged");
  const [editRow, setEditRow] = useState(null);
  const [totalDialog, setTotalDialog] = useState(false);
  const [totalForm, setTotalForm] = useState({ amount: "", note: "" });
  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [supersedeDialog, setSupersedeDialog] = useState(false);
  const [supersedeReason, setSupersedeReason] = useState("");
  const [teachOpen, setTeachOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [b, r] = await Promise.all([
        api.get(`/commission/imports/${batchId}`),
        api.get(`/commission/imports/${batchId}/rows`),
      ]);
      setBatch(b.data);
      setRows(r.data);
    } catch (e) {
      toast.error("Batch not found");
      navigate("/commissions/imports");
    }
  };
  useEffect(() => { load(); }, [batchId]);
  useEffect(() => {
    if (batch?.status !== "extracting") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [batch?.status]);

  if (!batch) return <p className="text-sm text-slate-400 animate-pulse">Loading batch…</p>;

  const isStmt = batch.doc_type === "carrier_statement";
  const canResolve = hasPerm("commission.resolve");
  const canImport = hasPerm("commission.import");
  const editable = !["superseded", "extracting", "failed"].includes(batch.status);
  const isFlagged = (r) => !r.verified && (r.validation_errors.length > 0 || r.duplicate_of || r.confidence < 0.8);
  const shown = rows.filter((r) =>
    filter === "all" ? true : filter === "transactions" ? r.row_type === "transaction" : isFlagged(r));

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const verifyRow = (r) => act(() => api.put(`/commission/imports/${batchId}/rows/${r.id}`, { verify: true }), "Row verified");

  const onSupersedeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", batch.doc_type);
    fd.append("period_key", batch.period_key);
    if (batch.carrier_id) fd.append("carrier_id", batch.carrier_id);
    fd.append("statement_id", batch.statement_id || "");
    fd.append("supersede_batch_id", batch.id);
    fd.append("supersede_reason", supersedeReason);
    setBusy(true);
    try {
      const { data } = await api.post("/commission/imports/upload", fd);
      toast.success("Reissued statement uploaded — old version superseded with lineage");
      setSupersedeDialog(false);
      navigate(`/commissions/imports/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const openOriginal = async () => {
    try {
      const { data } = await api.get(`/commission/imports/${batchId}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not load the original document");
    }
  };

  return (
    <div data-testid="commission-batch-page">
      <Link to="/commissions/imports" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"><ArrowLeft size={15} /> All imports</Link>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2 break-all">{batch.filename} <CCStatusBadge status={batch.status} /></h1>
          <p className="text-xs text-slate-500 mt-1">
            {isStmt ? `Carrier statement — ${batch.carrier_name}` : "EZLynx book of business"} · period {batch.period_key}
            {batch.statement_id && ` · stmt ${batch.statement_id}`} · uploaded by {batch.uploaded_by_name} {fmtDate(batch.created_at)}
            {batch.document && <> · sha256 <span className="font-mono">{batch.document.sha256?.slice(0, 12)}…</span>{batch.document.pages > 0 && ` · ${batch.document.pages} pages`}</>}
          </p>
          {batch.error && <p className="text-xs text-red-600 mt-1" data-testid="cc-batch-error">Extraction error: {batch.error}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={openOriginal} data-testid="cc-view-original-btn"><Eye size={15} className="mr-1" /> Original</Button>
          {batch.status === "failed" && canImport && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => act(() => api.post(`/commission/imports/${batchId}/retry`), "Re-extraction started")} data-testid="cc-retry-btn">
              <ArrowCounterClockwise size={15} className="mr-1" /> Retry
            </Button>)}
          {isStmt && canImport && !["superseded", "extracting", "validated"].includes(batch.status) &&
            !batch.filename?.toLowerCase().endsWith(".pdf") && (
            <Button variant="outline" size="sm" onClick={() => setTeachOpen(true)} data-testid="cc-teach-layout-btn">
              <GraduationCap size={15} className="mr-1" /> Teach layout
            </Button>)}
          {editable && canImport && (
            <Button variant="outline" size="sm" onClick={() => setSupersedeDialog(true)} data-testid="cc-supersede-btn"><FileArrowUp size={15} className="mr-1" /> Reissue</Button>)}
          {batch.status !== "superseded" && canImport && (
            <Button variant="outline" size="sm" className="text-red-600" onClick={() => setVoidDialog(true)} data-testid="cc-void-btn"><Trash size={15} className="mr-1" /> Void</Button>)}
        </div>
      </div>

      {batch.status === "extracting" ? (
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-6 text-center text-sky-800 text-sm animate-pulse" data-testid="cc-extracting-banner">
          Extracting rows from the document… PDFs are read page by page with AI vision. This view refreshes automatically.
        </div>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h2 className="font-medium text-sm mb-3">Reconciliation controls</h2>
              <div className="space-y-2.5" data-testid="cc-controls-list">
                {(batch.validations || []).map((v) => (
                  <div key={v.control} className="flex items-start gap-2 text-sm">
                    {CONTROL_ICON[v.status]}
                    <div>
                      <p className="font-medium">{v.label}</p>
                      <p className="text-xs text-slate-500">{v.detail}</p>
                    </div>
                  </div>
                ))}
                {(batch.validations || []).length === 0 && <p className="text-sm text-slate-400">No controls computed yet.</p>}
              </div>
              {editable && canResolve && (
                <div className="flex gap-2 mt-4 flex-wrap">
                  {isStmt && <Button variant="outline" size="sm" onClick={() => { setTotalForm({ amount: batch.source_total || "", note: "" }); setTotalDialog(true); }} data-testid="cc-set-total-btn">Set statement total</Button>}
                  <Button size="sm" disabled={busy || batch.status === "validated"}
                    onClick={() => act(() => api.post(`/commission/imports/${batchId}/validate`))} data-testid="cc-validate-btn">
                    <SealCheck size={15} className="mr-1" /> {batch.status === "validated" ? "Validated" : "Run validation"}
                  </Button>
                </div>
              )}
              {batch.status === "validated" && <p className="text-xs text-emerald-700 mt-2">Validated by {batch.validated_by_name} {fmtDate(batch.validated_at)}. Editing any row reverts to needs-review.</p>}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h2 className="font-medium text-sm mb-3">Totals</h2>
              <div className="grid grid-cols-2 gap-y-2 text-sm" data-testid="cc-totals-panel">
                {isStmt && <>
                  <span className="text-slate-500">Statement total {batch.source_total_origin && <Badge className="ml-1 bg-slate-100 text-slate-600">{batch.source_total_origin}</Badge>}</span>
                  <span className="text-right font-medium">{batch.source_total ? fmtAmt(batch.source_total) : <span className="text-amber-600 text-xs">missing — required</span>}</span>
                </>}
                <span className="text-slate-500">Extracted rows sum</span><span className="text-right font-medium">{isStmt ? fmtAmt(batch.extracted_total) : "n/a"}</span>
                {isStmt && <>
                  <span className="text-slate-500">Positives</span><span className="text-right">{fmtAmt(batch.positives_total)}</span>
                  <span className="text-slate-500">Negatives (chargebacks)</span><span className="text-right">{fmtAmt(batch.negatives_total)}</span>
                  <span className="text-slate-500">Difference</span>
                  <span className={`text-right font-semibold ${batch.discrepancy && Number(batch.discrepancy) !== 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="cc-discrepancy">
                    {batch.discrepancy == null ? "—" : fmtAmt(batch.discrepancy)}
                  </span>
                </>}
                <span className="text-slate-500">Rows (transactions)</span><span className="text-right">{batch.row_count} ({batch.transaction_count})</span>
                <span className="text-slate-500">Excluded / duplicates / low-conf</span>
                <span className="text-right">{batch.excluded_count} / {batch.duplicate_count} / {batch.low_confidence_count}</span>
              </div>
              {batch.source_total_note && <p className="text-xs text-slate-400 mt-2">Total source: {batch.source_total_note}</p>}
              {isStmt && batch.source_total && batch.extracted_total &&
                Number(batch.source_total) !== 0 &&
                Math.abs(Number(batch.source_total) + Number(batch.extracted_total)) < 0.005 &&
                Number(batch.source_total) !== Number(batch.extracted_total) && (
                <p className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded p-2.5 mt-2" data-testid="cc-sign-flip-hint">
                  The document total and the rows sum are the same amount with opposite signs. Many carriers print the
                  grand total in parentheses from <em>their</em> ledger's perspective (a balance due <em>to</em> your agency).
                  If the carrier actually paid {fmtAmt(Math.abs(Number(batch.extracted_total)).toFixed(2))}, confirm the
                  statement total as the positive amount via "Set statement total".
                </p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-medium text-sm">Extracted rows</h2>
              <div className="flex gap-1 items-center">
                {canResolve && editable && isStmt && (
                  <Button variant="outline" size="sm" className="h-7 text-xs mr-2"
                    onClick={() => setAddOpen(true)} data-testid="cc-add-row-btn">+ Add missing row</Button>
                )}
                {[["flagged", "Flagged"], ["transactions", "Transactions"], ["all", "All rows"]].map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)} data-testid={`cc-filter-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${filter === k ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {l}{k === "flagged" && ` (${rows.filter(isFlagged).length})`}
                  </button>
                ))}
              </div>
            </div>
            {shown.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">{filter === "flagged" ? "No flagged rows — everything is resolved." : "No rows."}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cc-rows-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                      <th className="px-3 py-2">Pg·#</th>
                      <th className="px-3 py-2">Policy</th>
                      <th className="px-3 py-2">Insured</th>
                      <th className="px-3 py-2">{isStmt ? "Type" : "Producer"}</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2 text-right">{isStmt ? "Commission" : "Premium"}</th>
                      <th className="px-3 py-2">Flags</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <tr key={r.id} className={`border-b border-slate-50 ${r.excluded ? "opacity-50" : ""} ${r.row_type !== "transaction" ? "bg-slate-50/50 text-slate-400" : ""}`}>
                        <td className="px-3 py-2 text-xs text-slate-400">{r.page}·{r.row_index}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.policy_number || "—"}</td>
                        <td className="px-3 py-2 max-w-[160px] truncate">{r.insured_name || "—"}</td>
                        <td className="px-3 py-2 text-xs">{isStmt ? (r.transaction_type || "—") : (r.assigned_producer || "—")}</td>
                        <td className="px-3 py-2 text-xs">{r.transaction_date || r.effective_date || "—"}</td>
                        <td className="px-3 py-2 text-right">{isStmt ? fmtAmt(r.amount) : fmtAmt(r.premium)}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1 flex-wrap">
                            {r.row_type !== "transaction" && <Badge className="bg-slate-100 text-slate-500">{r.row_type}</Badge>}
                            {r.excluded && <Badge className="bg-slate-200 text-slate-600">excluded</Badge>}
                            {r.duplicate_of && !r.verified && <Badge className="bg-orange-100 text-orange-700">dup?</Badge>}
                            {r.confidence < 0.8 && !r.verified && <Badge className="bg-amber-100 text-amber-800">low conf {Math.round(r.confidence * 100)}%</Badge>}
                            {r.validation_errors.length > 0 && !r.verified && <Badge className="bg-red-100 text-red-700" title={r.validation_errors.join("; ")}>{r.validation_errors.length} issue{r.validation_errors.length > 1 ? "s" : ""}</Badge>}
                            {r.verified && <Badge className="bg-emerald-100 text-emerald-700">verified</Badge>}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {canResolve && editable && (
                            <div className="flex gap-1 justify-end">
                              {isFlagged(r) && (
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-700" disabled={busy}
                                  onClick={() => verifyRow(r)} data-testid={`cc-verify-row-${r.id}`}>
                                  <CheckCircle size={15} className="mr-1" /> Verify
                                </Button>)}
                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditRow(r)} data-testid={`cc-edit-row-${r.id}`}>
                                <PencilSimple size={15} />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <RowEditDialog row={editRow} batch={batch} open={!!editRow} onClose={() => setEditRow(null)} onSaved={load} />
      <TeachLayoutDialog batch={batch} open={teachOpen} onClose={() => setTeachOpen(false)} onSaved={load} />
      <AddRowDialog batch={batch} open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />

      <Dialog open={totalDialog} onOpenChange={setTotalDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Set statement total (independent control)</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">This must come from the original statement or another documented source — never invented. $0.01 differences block validation.</p>
          <Input placeholder="e.g. 10250.00" value={totalForm.amount} onChange={(e) => setTotalForm({ ...totalForm, amount: e.target.value })} data-testid="cc-total-amount-input" />
          <Textarea placeholder="Where does this total come from? (required, min 10 chars)" value={totalForm.note}
            onChange={(e) => setTotalForm({ ...totalForm, note: e.target.value })} data-testid="cc-total-note-input" />
          <Button disabled={!totalForm.amount || totalForm.note.trim().length < 10 || busy}
            onClick={() => act(async () => { await api.post(`/commission/imports/${batchId}/source-total`, totalForm); setTotalDialog(false); }, "Statement total recorded")}
            data-testid="cc-total-save-btn">Save total</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={voidDialog} onOpenChange={setVoidDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Void this import?</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">The original file and every extracted row are retained for audit — the batch just stops counting toward the period.</p>
          <Textarea placeholder="Reason (required)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="cc-void-reason-input" />
          <Button variant="destructive" disabled={!voidReason.trim() || busy}
            onClick={() => act(async () => { await api.post(`/commission/imports/${batchId}/void`, { reason: voidReason }); setVoidDialog(false); }, "Batch voided")}
            data-testid="cc-void-confirm-btn">Void import</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={supersedeDialog} onOpenChange={setSupersedeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Upload reissued statement</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">The carrier sent a corrected version? Upload it here — this batch is superseded with explicit lineage, nothing is deleted.</p>
          <Textarea placeholder="Reason for reissue (required)" value={supersedeReason} onChange={(e) => setSupersedeReason(e.target.value)} data-testid="cc-supersede-reason-input" />
          <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx" className="hidden" onChange={onSupersedeFile} />
          <Button disabled={!supersedeReason.trim() || busy} onClick={() => fileRef.current?.click()} data-testid="cc-supersede-file-btn">
            <FileArrowUp size={15} className="mr-1" /> Choose reissued file
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
